import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class EncryptionService {
  private readonly HKDF_INFO: Uint8Array<ArrayBuffer> =
    new TextEncoder().encode('ecies-p256-aesgcm-v1');

  public async encrypt(payload: object): Promise<string> {
    const subtle = crypto.subtle;
    const plaintext = new TextEncoder().encode(JSON.stringify(payload));
    const recipientPub = await this._importRecipientKey(environment.encryption);
    const ephemeral = await subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveBits'],
    );
    const sharedZ = await subtle.deriveBits(
      { name: 'ECDH', public: recipientPub },
      ephemeral.privateKey,
      256,
    );
    const hkdfKey = await subtle.importKey('raw', sharedZ, 'HKDF', false, [
      'deriveBits',
    ]);
    const aesBits = await subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(0),
        info: this.HKDF_INFO,
      },
      hkdfKey,
      256,
    );
    const aesKey = await subtle.importKey(
      'raw',
      aesBits,
      { name: 'AES-GCM' },
      false,
      ['encrypt'],
    );

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = new Uint8Array(
      await subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext),
    );

    const ephemeralRaw = new Uint8Array(
      await subtle.exportKey('raw', ephemeral.publicKey),
    ); // 65 B
    const token = new Uint8Array(
      ephemeralRaw.length + iv.length + ciphertext.length,
    );
    token.set(ephemeralRaw, 0);
    token.set(iv, ephemeralRaw.length);
    token.set(ciphertext, ephemeralRaw.length + iv.length);

    return this._bytesToB64url(token);
  }

  private _b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
    const norm = b64.trim().replace(/-/g, '+').replace(/_/g, '/');
    const padded = norm.padEnd(
      norm.length + ((4 - (norm.length % 4)) % 4),
      '=',
    );
    return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
  }

  private _bytesToB64url(bytes: Uint8Array): string {
    let bin = '';
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private _importRecipientKey(
    publicKey: string | JsonWebKey,
  ): Promise<CryptoKey> {
    const algorithm = { name: 'ECDH', namedCurve: 'P-256' };

    if (typeof publicKey === 'string') {
      const raw = this._b64ToBytes(publicKey);
      if (raw.length !== 65 || raw[0] !== 0x04) {
        throw new Error(
          'La pública raw debe ser un punto X9.62 sin comprimir de 65 bytes (0x04‖X‖Y).',
        );
      }
      return crypto.subtle.importKey('raw', raw, algorithm, false, []);
    }

    return crypto.subtle.importKey('jwk', publicKey, algorithm, false, []);
  }
}
